import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react';

const NotificationContext = createContext(null);

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info
};

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback(({ type = 'info', title, message, duration = 3200 } = {}) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const toast = { id, type, title, message };
    setToasts((prev) => [toast, ...prev].slice(0, 4));
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const confirm = useCallback(({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', tone = 'danger' } = {}) =>
    new Promise((resolve) => {
      setConfirmState({ title, message, confirmText, cancelText, tone, resolve });
    }), []);

  const value = useMemo(() => ({ notify, confirm }), [notify, confirm]);

  const handleConfirm = () => {
    if (confirmState?.resolve) confirmState.resolve(true);
    setConfirmState(null);
  };

  const handleCancel = () => {
    if (confirmState?.resolve) confirmState.resolve(false);
    setConfirmState(null);
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[9999] space-y-3">
        {toasts.map((toast) => {
          const Icon = ICONS[toast.type] || Info;
          const toneClass =
            toast.type === 'success' ? 'border-emerald-500 text-emerald-700' :
            toast.type === 'error' ? 'border-rose-500 text-rose-700' :
            toast.type === 'warning' ? 'border-amber-500 text-amber-700' :
            'border-blue-500 text-blue-700';
          return (
            <div
              key={toast.id}
              className={`min-w-[280px] max-w-[360px] rounded-xl border backdrop-blur-md shadow-xl p-4 ${toneClass}`}
              style={{ borderColor: 'var(--card-border)', background: 'var(--card-bg)' }}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  {toast.title && (
                    <div className="text-sm font-semibold" style={{ color: 'var(--accent-text)' }}>
                      {toast.title}
                    </div>
                  )}
                  {toast.message && (
                    <div className="text-xs mt-1" style={{ color: 'var(--accent-text-subtle)' }}>
                      {toast.message}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeToast(toast.id)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Dismiss notification"
                >
                  ×
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm Modal */}
      {confirmState && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCancel} />
          <div
            className="relative w-full max-w-md rounded-2xl border shadow-2xl p-6"
            style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
          >
            <div className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>
              {confirmState.title || 'Please confirm'}
            </div>
            <div className="text-sm mt-2" style={{ color: 'var(--accent-text-subtle)' }}>
              {confirmState.message || 'Are you sure you want to continue?'}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg border text-sm font-medium"
                style={{ borderColor: 'var(--card-border)', color: 'var(--accent-text)' }}
              >
                {confirmState.cancelText}
              </button>
              <button
                onClick={handleConfirm}
                className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                  confirmState.tone === 'danger' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used within NotificationProvider');
  return ctx;
};
